/**
 * auth-expired.interceptor.ts
 * ----------------------------
 * Angular HTTP interceptor that detects a session that has gone stale
 * server-side (e.g. the user logged out in another tab) and forces the
 * client back to a logged-out state, instead of leaving a tab stuck
 * showing a broken/empty authenticated page.
 *
 * Only acts when the client currently believes it's logged in
 * (UserService.currentUser is set) and a request comes back 401 — a 401
 * while currentUser is unset is normal, expected behavior for anonymous
 * browsing (e.g. the initial getCurrentUser() check) and must not trigger
 * a redirect.
 *
 * Register in app.config.ts alongside the other interceptors.
 */

import {
  HttpErrorResponse,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { UserService } from '../../services/user.service';

export const authExpiredInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
) => {
  const userService = inject(UserService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse && error.status === 401 && userService.currentUser) {
        userService.clearCurrentUser();
        router.navigateByUrl('/');
      }

      return throwError(() => error);
    }),
  );
};
